/**
 * Traveler proof: the testimonials, the gate that decides whether they are
 * published, and the one place a host agency's URL is written down.
 *
 * Lives in `shared` for the same reason `site.ts` does: the web bundle and the
 * API both have to agree on it. The web app decides whether to register the
 * route, render the band and link to it; the API decides whether `/sitemap.xml`
 * advertises it. One answer, one file.
 *
 * Nothing in here may be invented. A testimonial is a traveler's exact words,
 * published with their permission, or it does not exist. An empty
 * `TESTIMONIALS` is the honest state until Ken has both.
 */
/**
 * Where Ken's bookings are currently written, or `null` to reference no
 * outside profile anywhere on the site.
 *
 * `name` is for whoever edits this file, not for the page: no visible copy
 * prints it. The agency behind a booking is an arrangement that can change,
 * and the public site should not have to be rewritten when it does, so the
 * source line reads "Ken's current advisor profile" and takes only the URL
 * from here.
 */
export const HOST_PROFILE = {
    name: 'Fora',
    profileUrl: 'https://www.foratravel.com/advisor/ken-mcgrady',
};
/**
 * How many approved testimonials it takes before any of this is public.
 *
 * Two, because one is not a section. A page headed "Traveler stories" holding
 * a single quote reads as a page that failed to load, and a page holding none
 * reads as a site that is not finished — which is worse for trust than having
 * no such page at all. Below this number nothing is registered, rendered,
 * linked or listed in the sitemap.
 */
export const MIN_PUBLISHED_TESTIMONIALS = 2;
/**
 * Published testimonials, newest first.
 *
 * ---------------------------------------------------------------------------
 * TODO (Ken): paste at least 2 approved testimonials here and delete this
 * comment. Adding them is the whole act of publishing: the route, the homepage
 * band, the link under the planning form and the sitemap entry all switch on
 * from this array and nothing else.
 *
 * Before anything goes in, two things have to be true: the words are exactly
 * what the traveler wrote, and reuse is permitted (check the terms wherever
 * the review was first published, or ask the traveler).
 *
 * One entry per testimonial, in this shape:
 *
 *   {
 *     id: 'japan-2026-sarah',
 *     quote: '<the traveler's exact words>',
 *     attribution: '<name as they agreed to be shown>',
 *     context: '<trip type and destination, e.g. "Family trip to Japan, 2026">',
 *     source: { label: 'Originally shared on Ken's advisor profile', url: HOST_PROFILE?.profileUrl },
 *   }
 *
 * Worth covering between them, if the approved quotes allow it: one about
 * communication and responsiveness, one about a hotel or a room choice, and
 * one about a specific destination. Do not stretch a quote to fill a slot.
 *
 * `source` is optional, and its label is free text: keep it host-neutral so a
 * change of agency does not date it. Drop it for a testimonial sent to Ken
 * directly.
 * ---------------------------------------------------------------------------
 */
export const TESTIMONIALS = [];
/**
 * Whether traveler stories are public at all.
 *
 * Consulted by the router, the homepage band, the planning form's proof line,
 * the prerender build and the sitemap, so there is no way for one of them to
 * advertise a page the others consider unfinished.
 */
export function travelerStoriesPublished(testimonials = TESTIMONIALS) {
    return testimonials.length >= MIN_PUBLISHED_TESTIMONIALS;
}
/**
 * What travelers consistently tell Ken, in his own words rather than theirs.
 *
 * Not a quote and not attributed to anyone, so it carries no reuse question
 * and no risk of putting words in a traveler's mouth. It is what the planning
 * form says whether or not any testimonial has been cleared, which is why it
 * is not gated on the array above.
 */
export const TRAVELER_VALUE_LINE = 'Travelers value quick communication, thoughtful hotel choices, and support throughout the trip.';
/**
 * The quiet line pointing at where the original reviews live.
 *
 * Deliberately does not name the agency: the sentence has to survive Ken
 * moving his book of business, and a reader following the link finds out
 * whose profile it is the moment it opens.
 */
export const TRAVELER_PROOF_SOURCE_LINE = 'Read the original traveler reviews on Ken’s current advisor profile.';
